// Client for the Worker's stored file drops (apps/worker/src/drops.ts,
// docs/adr/0015). Tokens go in Authorization headers, never in URLs; bodies
// are ciphertext sealed by @sss/crypto.

import type { CreateDropRequest, CreateDropResponse, DropClaim, DropConfig, DropMetadata, DropStatus } from '@sss/types';
import { API_BASE_URL } from './config';
import { SecretsApiError, json, request } from './secretsApi';

export type DropProblem =
  | 'passPending'
  | 'passNotFound'
  | 'passExpired'
  | 'allowanceExceeded'
  | 'tooLarge'
  | 'challengeFailed'
  | 'rateLimited'
  | 'unavailable'
  | 'network'
  | 'other';

/** Why creating or uploading a drop failed, from the Worker's status codes. */
export const dropProblem = (err: unknown): DropProblem => {
  if (!(err instanceof SecretsApiError)) return 'other';
  if (err.type === 'network' || err.type === 'rateLimited' || err.type === 'passPending' || err.type === 'challengeFailed' || err.type === 'tooLarge') return err.type;
  const byStatus: Record<number, DropProblem> = { 402: 'allowanceExceeded', 404: 'passNotFound', 410: 'passExpired', 503: 'unavailable' };
  return byStatus[err.status] ?? 'other';
};

/** Worth trying again: the network, or the server having a moment. Anything else won't change on a retry. */
export const isRetryable = (err: unknown): boolean => err instanceof SecretsApiError && (err.type === 'network' || err.status === 429 || err.status >= 500);

const path = (id: string, suffix = '') => `/v1/drops/${encodeURIComponent(id)}${suffix}`;
const bearer = (token: string, method: string, body?: BodyInit): RequestInit => ({ method, headers: { Authorization: `Bearer ${token}` }, body });

export const getDropConfig = (base?: string) => request<DropConfig>('/v1/drops', {}, base);

export const createDrop = (input: CreateDropRequest, base?: string) => request<CreateDropResponse>('/v1/drops', json('POST', input), base);

/**
 * One upload part (1-based), exactly its size. XMLHttpRequest, not fetch:
 * only XHR reports upload progress, and on a slow connection a 16 MiB part
 * can take minutes, so the page needs to count bytes, not parts.
 */
export const putDropPart = (
  id: string,
  part: number,
  body: Uint8Array<ArrayBuffer>,
  uploadToken: string,
  signal?: AbortSignal,
  onProgress?: (sent: number) => void,
  base = API_BASE_URL,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason);
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${base}${path(id, `/parts/${part}`)}`);
    xhr.setRequestHeader('Authorization', `Bearer ${uploadToken}`);
    xhr.upload.onprogress = (e) => onProgress?.(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let type: string | undefined;
      try {
        type = (JSON.parse(xhr.responseText) as { type?: string }).type;
      } catch {
        type = undefined;
      }
      reject(new SecretsApiError(xhr.status === 429 ? 'rateLimited' : xhr.status === 413 ? 'tooLarge' : 'other', xhr.status, type ?? `HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new SecretsApiError('network', 0, 'Network error'));
    xhr.ontimeout = xhr.onerror;
    signal?.addEventListener('abort', () => {
      xhr.abort();
      reject(signal.reason);
    });
    xhr.send(body);
  });

export const completeDrop = (id: string, uploadToken: string, base?: string) => request<{ endsAt: string }>(path(id, '/complete'), bearer(uploadToken, 'POST'), base);

/** Delete now, with the sender or upload token. */
export const deleteDrop = (id: string, token: string, base?: string) => request<void>(path(id), bearer(token, 'DELETE'), base);

/** For the sender: downloads, the end date, the charge. */
export const getDropStatus = (id: string, senderToken: string, base?: string) => request<DropStatus>(path(id, '/status'), bearer(senderToken, 'GET'), base);

/** The header and sealed manifest. Never uses a download. */
export const getDropMetadata = (id: string, base?: string) => request<DropMetadata>(path(id), {}, base);

/** Start a download: one fewer left, and a token for its window. */
export const claimDrop = (id: string, base?: string) => request<DropClaim>(path(id, '/claim'), { method: 'POST' }, base);

/** Tell the server a download finished; the last one deletes the drop. */
export const finishDropDownload = (id: string, downloadToken: string, base?: string) => request<{ done: boolean }>(path(id, '/done'), bearer(downloadToken, 'POST'), base);

/** Bytes [start, end] of the sealed data. Binary, so not through `request`. */
export const readDropRange = async (id: string, downloadToken: string, start: number, end: number, signal?: AbortSignal, base = API_BASE_URL): Promise<Uint8Array> => {
  let res: Response;
  try {
    res = await fetch(`${base}${path(id, '/data')}`, {
      headers: { Authorization: `Bearer ${downloadToken}`, Range: `bytes=${start}-${end}` },
      referrerPolicy: 'no-referrer',
      cache: 'no-store',
      signal,
    });
    if (res.status === 206) return new Uint8Array(await res.arrayBuffer());
  } catch {
    throw new SecretsApiError('network', 0, 'Network error');
  }
  throw new SecretsApiError(res.status === 404 ? 'gone' : 'other', res.status, `HTTP ${res.status}`);
};
